/**
 * useInviteMember hook
 *
 * Handles inviting a new member to the organization.
 */

"use client";

import { useState, useCallback } from "react";
import type { OrgRole } from "@/lib/organizations/types";

interface UseInviteMemberReturn {
  isInviting: boolean;
  error: string | null;
  invite: (email: string, role: OrgRole) => Promise<boolean>;
}

export function useInviteMember(onSuccess?: () => void): UseInviteMemberReturn {
  const [isInviting, setIsInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invite = useCallback(
    async (email: string, role: OrgRole): Promise<boolean> => {
      setIsInviting(true);
      setError(null);

      try {
        const res = await fetch("/api/tenant/users/invite", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, role }),
        });

        if (!res.ok) {
          const json = await res.json().catch(() => ({}));
          throw new Error(json.error || "Einladung konnte nicht gesendet werden");
        }

        onSuccess?.();
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Einladung konnte nicht gesendet werden");
        return false;
      } finally {
        setIsInviting(false);
      }
    },
    [onSuccess]
  );

  return {
    isInviting,
    error,
    invite,
  };
}
